import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { formatDate } from "@angular/common";
import { MessageService } from "primeng/api";
import { CatExpertRouterLink } from "../../app-routing-enum";
import {
  keysToSelectedFolder,
  newFolderDialogContent,
} from "../../main-container/studio/studio-constants";
import { HttpService } from "../../services/http.service";
import { ProjectOverviewService } from "../../services/project-overview.service";
import { StateService } from "../../services/state.service";

@Component({
  selector: "app-common-browse-experiments",
  templateUrl: "./common-browse-experiments.component.html",
  styleUrl: "./common-browse-experiments.component.scss",
})
export class CommonBrowseExperimentsComponent implements OnChanges {
  @Input() dialogContent: any = {};
  @Input() showDialog: boolean = false;
  @Input() data: any = [];
  @Input() preSelected: any = [];
  @Output() closeDialog = new EventEmitter<any>();

  parentId: string = "root";
  selectedExperiments: any[] = [];
  selectedOnlyExperiments: any[] = []; // Assign the experiments when check the checkbox by experiments wise

  experimentList: { [key: string]: any[] } = {
    experiments: [],
    shared: [],
  };

  currentTab: "shared" | "experiments" =
    CatExpertRouterLink.Experiments as "experiments";

  breadcrumbTrailMap: { [key: string]: any[] } = {
    shared: [],
    experiments: [],
  };

  keysToSelectedFolder = keysToSelectedFolder;
  newFolderDialogContent = newFolderDialogContent;
  wholeFolderData: any = {};
  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 1;
  totalRecords: number = 0;
  selectedList: any = JSON.parse(sessionStorage.getItem("storeIds")!) || [];
  folderSelectionMap: { [id: string]: boolean } = {};
  experimentSelectionMap: { [id: string]: boolean } = {};
  showTableFilters: boolean = false;
  keyAccountList: any = [];
  plansList: any[] = [];
  selectedPlans: any[] = [];
  planogramUsers: { [id: string]: any } = {};
  enableExpSelection: boolean = true;
  originalTableCols: any[] = [];
  plansMapforExperiment: { [experimentId: string]: any[] } = {};
  selectedPlansforExperiment: { [experimentId: string]: any[] } = {};

  constructor(
    private httpService: HttpService,
    public stateService: StateService,
    private messageService: MessageService,
    private projectService: ProjectOverviewService
  ) {
    this.getKeyAccounts();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes["data"]) {
      let response = changes["data"].currentValue;
      this.wholeFolderData = {
        shared: response.shared_contents,
        experiments: response.my_contents,
      };
      this.initializeSelectionMaps();
      this.resetToRoot();

      // Apply preselection after data is ready
      setTimeout(() => {
        this.applyPreSelectedExperimentsAndPlans();
      }, 0); // Ensure it's run after view updates
    }
    this.originalTableCols = JSON.parse(
      JSON.stringify(this.dialogContent.tableColumns)
    );
  }

  initializeSelectionMaps() {
    this.folderSelectionMap = {}; // no pre-selection for folders
    this.experimentSelectionMap = {};

    // Populate experimentSelectionMap with true for IDs in selectedList
    this.selectedList.forEach((id: string) => {
      this.experimentSelectionMap[id] = true;
    });
  }

  applyPreSelectedExperimentsAndPlans() {
    if (!this.preSelected || typeof this.preSelected !== "object") return;

    Object.keys(this.preSelected).forEach((expId) => {
      const planIds = this.preSelected[expId];

      // Mark experiment as selected
      this.experimentSelectionMap[expId] = true;

      // Try to find the experiment in your current tree and add to selected
      const experiment = this.findExperimentById(
        this.experimentList[this.currentTab],
        expId
      );

      if (experiment) {
        experiment.isChecked = true;
        if (!this.selectedExperiments.find((e) => e.id === experiment.id)) {
          this.selectedExperiments.push(experiment);
          this.selectedOnlyExperiments.push(experiment);
        }

        // Also mark parent folders as selected (for UI checkbox)
        this.markParentFoldersAsSelected(expId);
      }

      // Fetch and preselect plans
      this.getPlansForExperiment([{ id: expId }]);
    });
  }

  findExperimentById(items: any[], id: string): any {
    for (const item of items) {
      if (item.id === id && item.experiment_type !== "folder") {
        return item;
      }

      if (item.children?.length) {
        const found = this.findExperimentById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  markParentFoldersAsSelected(expId: string) {
    const walk = (items: any[]): boolean => {
      let found = false;

      for (const item of items) {
        if (item.experiment_type === "folder" && item.children?.length) {
          item.isChecked = this.folderSelectionMap[item.id] || false;
          const childFound = walk(item.children);
          if (childFound) {
            this.folderSelectionMap[item.id] = true;
            found = true;
          }
        } else if (item.id === expId) {
          return true;
        }
      }

      return found;
    };

    walk(this.experimentList[this.currentTab]);
  }

  processExperimentData(sectionData: any): any[] {
    if (!this.enableExpSelection) {
      this.experimentList[this.currentTab] = this.selectedExperiments;
    }

    if (!sectionData) return [];

    const processItems = (items: any[]): any[] => {
      return (items || []).map((item: any) => {
        const isFolder =
          item?.type === "folder" ||
          item?.experiment_type === "folder" ||
          item?.experiments;

        const children = item.children ? processItems(item.children) : [];

        // Determine isChecked:
        // - For experiments: check experimentSelectionMap
        // - For folders: selected if all children are selected
        let isChecked = false;
        if (isFolder) {
          const childItems = [
            ...(item.folders || []),
            ...(item.experiments || []),
            ...children,
          ];

          const allChildrenSelected =
            childItems.length > 0 &&
            childItems.every((child) =>
              child.experiment_type === "folder"
                ? this.folderSelectionMap[child.id]
                : this.experimentSelectionMap[child.id]
            );

          isChecked =
            allChildrenSelected || this.folderSelectionMap[item.id] || false;
          this.folderSelectionMap[item.id] = isChecked;
        } else {
          isChecked = this.experimentSelectionMap[item.id] || false;
        }

        return {
          ...item,
          experiment_type: isFolder ? "folder" : item?.experiment_type,
          isEditable: false,
          isLoading: false,
          isRowDisabled: item.id === sessionStorage.getItem("expId"),
          isChecked,
          hasLoadedChildren: !!item.children,
          children,
        };
      });
    };

    const folders = processItems(sectionData.folders || []);
    const experiments = processItems(sectionData.experiments || []);

    this.prepareTableFilterOptions();
    return [...folders, ...experiments];
  }

  // Function for get the key accounts
  getKeyAccounts() {
    this.projectService.getUserKeyAccounts().subscribe((val) => {
      if (val) {
        this.keyAccountList = val;
        this.prepareTableFilterOptions();
      }
    });
  }

  // Function to render filter dropdown options
  prepareTableFilterOptions() {
    this.dialogContent.tableColumns.map((each: any) => {
      if (each.filterType === "dropdown") {
        if (each.value === "key_account") {
          each.filterOptions = this.keyAccountList.map((item: any) => ({
            label: item.name,
            value: item.id,
          }));
        } else if (each.value === "country") {
          const seen = new Set();
          each.filterOptions = this.keyAccountList
            .filter((item: any) => {
              const key = item.country;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .map((item: any) => ({
              label: item.country,
              value: item.country,
            }));
        } else if (each.value === "region") {
          const seen = new Set();
          each.filterOptions = this.keyAccountList
            .filter((item: any) => {
              const key = item.region;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .map((item: any) => ({
              label: item.region,
              value: item.region,
            }));
        }
      }
    });
  }

  dialogTabChange(tabKey: "shared" | "experiments") {
    this.currentTab = tabKey;
    this.resetToRoot();
  }

  navigateExperiment(event: any) {
    const experiment = event.data;

    if (experiment.experiment_type === "folder") {
      // Update breadcrumb trail
      this.breadcrumbTrailMap[this.currentTab].push({
        id: experiment.id,
        label: experiment.name,
        section: this.currentTab,
      });

      this.parentId = experiment.id;
      this.experimentList[this.currentTab] =
        this.processExperimentData(experiment);
      this.showTableFilters = false;
    }
  }

  onNavigateBack() {
    const trail = this.breadcrumbTrailMap[this.currentTab];

    trail.pop(); // Remove current folder from breadcrumb

    const parentFolder = trail[trail.length - 1];
    const parent = this.findFolderById(
      this.processExperimentData(this.wholeFolderData[this.currentTab]),
      parentFolder.id
    );

    if (parent) {
      this.parentId = parent.id;
      this.experimentList[this.currentTab] = this.processExperimentData(parent);
    } else {
      // Fallback to root
      this.resetToRoot();
    }
  }

  resetToRoot() {
    this.parentId = "root";
    // Initialize breadcrumb trail with root label
    this.breadcrumbTrailMap[this.currentTab] = [
      {
        id: "root",
        label: this.currentTab === "experiments" ? "My Experiments" : "Shared",
        section: this.currentTab,
      },
    ];
    this.experimentList[this.currentTab] = this.processExperimentData(
      this.wholeFolderData[this.currentTab]
    );
  }

  findFolderById(items: any[], id: string): any {
    for (const item of items) {
      if (item.id === id && item.experiment_type === "folder") {
        return item;
      }
      if (item.children?.length) {
        const found = this.findFolderById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  multipleActions(event: any) {
    const selectedItems = Array.isArray(event.data) ? event.data : [event.data];
    const selectedCheckedItems = selectedItems.filter(
      (item: any) => item.isChecked
    );
    const newExperiments = selectedCheckedItems.filter((item: any) => {
      return ["planogram", "space_assortment"].includes(item.experiment_type);
    });

    // Step 2: Combine old and new, avoiding duplicates based on unique property (e.g., id)
    newExperiments.forEach((expData: any) => {
      if (!this.selectedExperiments.some((item) => item.id === expData.id)) {
        this.selectedExperiments.push(expData);

        // Assign the experiments when check the checkbox by experiments wise
        this.selectedOnlyExperiments.push(expData);
      }
    });

    const unSelectedExperiments = selectedItems.filter(
      (item: any) => !item.isChecked
    );

    if (selectedCheckedItems.length) {
      selectedCheckedItems.forEach((item: any) => {
        if (
          (item.experiment_type === "folder" ||
            item.experiment_type === undefined) &&
          !["planogram", "space_assortment"].includes(item.experiment_type)
        ) {
          // Set folder selection state
          this.folderSelectionMap[item.id] = item.isChecked;
          // Propagate folder selection to children (folders & experiments)
          this.toggleChildren(item, item.isChecked);
        } else {
          // Set experiment selection state
          this.experimentSelectionMap[item.id] = item.isChecked;
        }
      });
    }

    if (unSelectedExperiments.length) {
      this.updateUncheckedSelections(unSelectedExperiments);
    }
  }

  // Method to update the un checked folder's and experiments
  updateUncheckedSelections(unSelectedExperiments: any) {
    unSelectedExperiments.forEach((item: any) => {
      if (
        (item.experiment_type === "folder" ||
          item.experiment_type === undefined) &&
        !["planogram", "space_assortment"].includes(item.experiment_type)
      ) {
        this.folderSelectionMap[item.id] = item.isChecked;
        this.toggleChildren(item, item.isChecked);
      } else {
        this.experimentSelectionMap[item.id] = item.isChecked;
        // Remove the folder experiments and nested folder experiments when checkbox is unchecked.
        this.selectedExperiments = this.selectedExperiments.filter(
          (exp) => exp.id !== item.id
        );

        // Remove the experiments when un-check the checkbox by experiments wise
        this.selectedOnlyExperiments = this.selectedOnlyExperiments.filter(
          (exp) => exp.id !== item.id
        );
        // Delete the plans from experiments when checkbox is unchecked
        delete this.selectedPlansforExperiment[item.id];
      }
    });
  }

  toggleChildren(folder: any, checked: boolean) {
    const children = [...(folder.folders || []), ...(folder.experiments || [])];

    children.forEach((child) => {
      if (
        (child.experiment_type === "folder" ||
          child.experiment_type === undefined) &&
        !["planogram", "space_assortment"].includes(child.experiment_type)
      ) {
        this.folderSelectionMap[child.id] = checked;
        this.toggleChildren(child, checked); // recursive call
      } else {
        this.experimentSelectionMap[child.id] = checked;
        // Pushing folder experiments and nested folder experiments when checkbox is checked
        if (checked) {
          if (!this.selectedExperiments.some((exp) => exp.id === child.id)) {
            this.selectedExperiments.push({ ...child });
            // Assign the experiments when check the checkbox by experiments wise
            this.selectedOnlyExperiments.push({ ...child });
          }
        } else {
          // Remove the folder experiments and nested folder experiments when checkbox is unchecked.
          this.selectedExperiments = this.selectedExperiments.filter(
            (exp) => exp.id !== child.id
          );

          // Remove the experiments when un-check the checkbox by experiments wise
          this.selectedOnlyExperiments = this.selectedOnlyExperiments.filter(
            (exp) => exp.id !== child.id
          );
          // Delete the plans from experiments when checkbox is unchecked
          delete this.selectedPlansforExperiment[child.id];
        }
      }
    });
  }

  addExperiments(btnFlag: boolean) {
    this.onClose(btnFlag);
  }

  onClose(btnFlag: boolean = false) {
    if (!btnFlag) {
      this.selectedExperiments = [];
      this.selectedPlansforExperiment = {};
      this.callProceedToEmit(btnFlag);
      return;
    }

    if (this.selectedOnlyExperiments.length > 0) {
      // This will call only when checkbox checked by experiments wise
      this.getPlansForExperiment(this.selectedOnlyExperiments, btnFlag);
    } else {
      // This will call only when checkbox checked by plans wise
      this.callProceedToEmit(btnFlag);
    }
  }

  // Helper method to emit the selected experiment and selected plans to parent component
  callProceedToEmit(btnFlag: boolean = false) {
    this.closeDialog.emit({
      action: btnFlag,
      selectedExperiments: this.selectedExperiments,
      plansMapforExperiment: this.selectedPlansforExperiment,
    });
    this.showDialog = false;
  }

  // Helper method to call the actions method
  getTableAction(event: { action: { callBack: keyof any }; data: any }) {
    const callback = event.action.callBack;

    if (
      typeof callback === "string" &&
      typeof (this as any)[callback] === "function"
    ) {
      (this as any)[callback](event);
    }
  }

  filterCallback(event: any) {
    this.showTableFilters = !this.showTableFilters;
  }

  getPlansForExperiment(selectedItems: any, btnFlag: boolean = false) {
    // Always normalize selectedItems into an array of experiments
    const experiments = Array.isArray(selectedItems)
      ? selectedItems
      : [selectedItems.data];

    const experimentIds = experiments.map((exp) => exp.id);

    const apiUrl = this.stateService.apiList.planogram.getPlanogramList;
    const payload = {
      experiment_ids: experimentIds,
      include_additional_details: true,
    };

    this.commonApiCall(apiUrl, "postMethod", payload, (response) => {
      if (response) {
        // experimentIds.forEach((id) => {
        //   this.plansMapforExperiment[id] = response[id]?.plans || [];
        // });
        experimentIds.forEach((id) => {
          const plans = response[id]?.plans || [];
          this.plansMapforExperiment[id] = plans;

          const preselectedPlanIds = this.preSelected?.[id] || [];

          this.selectedPlansforExperiment[id] = plans.filter((plan: any) => {
            const isPreselected = preselectedPlanIds.includes(plan.id);
            if (isPreselected) {
              plan["isChecked"] = true;
            }
            return isPreselected;
          });
        });

        this.getBulkUserList(btnFlag, experimentIds);
      }
    });
  }

  getBulkUserList(btnFlag: boolean = false, experimentIds: any) {
    const userIds = new Set<string>();
    Object.values(this.plansMapforExperiment).forEach((exp: any) => {
      if (Array.isArray(exp)) {
        exp.forEach((plan: any) => {
          const userId = plan.created_by || plan.user;
          if (userId) {
            userIds.add(userId);
          }
        });
      }
    });
    const payload = {
      users_list: Array.from(userIds),
    };
    this.stateService.addCalledApi("get-users");
    this.stateService.getBulkUserDetailsService(payload).subscribe((val) => {
      if (val) {
        this.stateService.removeCalledApi("get-users");
        val.forEach((userDetails: any) => {
          this.planogramUsers[userDetails.id] = userDetails;
        });

        Object.values(this.plansMapforExperiment).forEach((exp: any) => {
          if (Array.isArray(exp)) {
            exp.forEach((plan: any) => {
              let createdBy = plan.created_by || plan.user;
              if (createdBy in this.planogramUsers) {
                plan["owner_by"] = this.planogramUsers[createdBy];
              }
              return plan;
            });
          }
        });

        // This will call, when exeriments only selected without plans selections
        if (btnFlag) {
          const filteredPlansMapforExperiment = Object.keys(
            this.plansMapforExperiment
          )
            .filter((key) => experimentIds.includes(key))
            .reduce((obj: any, key) => {
              obj[key] = this.plansMapforExperiment[key];
              return obj;
            }, {});

          this.selectedPlansforExperiment = {
            ...this.selectedPlansforExperiment,
            ...filteredPlansMapforExperiment,
          };

          this.callProceedToEmit(btnFlag);
        }
      }
    });
  }

  formatHumanReadableDate(dateStr: string): string {
    const inputDateObj = new Date(dateStr + "Z");
    const now = new Date();

    const inputDay = new Date(
      inputDateObj.getFullYear(),
      inputDateObj.getMonth(),
      inputDateObj.getDate()
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    let label: string;

    if (inputDay.getTime() === today.getTime()) {
      label = "Today";
    } else if (inputDay.getTime() === yesterday.getTime()) {
      label = "Yesterday";
    } else {
      // Optional: format older dates like "Jul 7, 2025"
      return formatDate(inputDateObj, "MMM d, y, h:mm a", "en-US");
    }

    const time = formatDate(inputDateObj, "h:mm a", "en-US");
    return `${label}, ${time}`;
  }

  toggleRowSelection(event: any, expData: any, plan: any) {
    const isChecked = event.checked;

    plan["isChecked"] = isChecked;

    if (isChecked) {
      if (!this.selectedExperiments.includes(expData)) {
        this.selectedExperiments.push(expData);
      }
    } else {
      this.selectedExperiments = this.selectedExperiments.filter(
        (item) => item.id !== expData.id
      );
    }

    if (!this.selectedPlansforExperiment[expData.id]) {
      this.selectedPlansforExperiment[expData.id] = [];
    }

    if (isChecked) {
      if (!this.selectedPlansforExperiment[expData.id].includes(plan)) {
        this.selectedPlansforExperiment[expData.id].push(plan);
      }
    } else {
      this.selectedPlansforExperiment[expData.id] =
        this.selectedPlansforExperiment[expData.id].filter(
          (item) => item.id !== plan.id
        );
    }
  }

  enableExperimentSelection() {
    this.enableExpSelection = true;
    this.dialogContent.tableColumns = this.originalTableCols;
    this.initializeSelectionMaps();
    this.resetToRoot();
  }

  getString(val: any, column?: any) {
    if (column && column?.value === "status") {
      let textVal = this.stateService.titleRemoveUnderscore(val);
      if (typeof textVal === "string") {
        return textVal != "" ? textVal : "";
      }
      return;
    }
    if (column && column.value === "experiment_type") {
      return val === "space_assortment" ? "Space Planogram" : val;
    }
    if (typeof val === "string") {
      return val != "" ? val : "";
    }

    return val != null ? val : "";
  }

  commonApiCall(
    apiUrl: string,
    method: string,
    params: any,
    callback: (response: any, params: any) => void
  ) {
    this.stateService.addCalledApi(apiUrl);
    this.httpService[method as keyof HttpService](apiUrl, params).subscribe(
      (response: any) => {
        this.stateService.removeCalledApi(apiUrl);
        callback(response, params);
      },
      (err: any) => {
        this.messageService.clear();
        if (err.status === 400) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: "Something went wrong!",
            detail: err.error.message,
            sticky: true,
          });
        } else if (err.status === 422) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: err.error.detail?.[0]?.msg || "Something went wrong!",
            detail: err.error.detail,
            sticky: true,
          });
        } else if (err.status !== 401) {
          this.messageService.add({
            key: "c",
            severity: "warn",
            summary: err.error?.message || "Something went wrong!",
            detail: err?.error?.traceback,
            sticky: true,
          });
        }
        this.stateService.removeCalledApi(apiUrl);
      }
    );
  }
}
